import { useState, useContext } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import { AuthContext } from '@/context/auth-context'
import OrganizationSetup from '@/organizations/views/create-organization/OrganizationSetup'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { postCreateOrganization } from '@/organizations/fetch/post-create-organization'
import { CreateSelfServeOrganizationRequest } from '@/organizations/schemas'

const CreateOrganizationCard = () => {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  const { refreshUserAccountData, userAccountData } = useContext(AuthContext)
  const isSubscriptionExempt = userAccountData?.isSubscriptionExempt

  const [orgName, setOrgName] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [emailValue, setEmailValue] = useState('')
  const [isOrgCreationInProgress, setIsOrgCreationInProgress] = useState(false)

  const handleCreateOrganization = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (orgName.trim()) {
      try {
        setIsOrgCreationInProgress(true)

        const invitee_emails = emails.map((email) => ({ invitee_email: email }))
        const createOrganizationRequest: CreateSelfServeOrganizationRequest = { organization: { name: orgName, invites: invitee_emails } }
        const result = await postCreateOrganization(createOrganizationRequest)

        if (result.organization) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
          refreshUserAccountData()
          trackEvent(AnalyticsEvent.OrganizationCreated, {
            organizationId: result.organization.id || '',
            organizationCreatedInvitesCount: invitee_emails.length,
          })

          dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Organization created.`, durationMs: kToastDurationShortMs }))
        }
      } catch (error) {
        console.error('Failed to create organization', error)
        dispatch(
          openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to create organization, please try again.', durationMs: kToastDurationMediumMs })
        )
      } finally {
        setIsOrgCreationInProgress(false)
      }
    }
  }

  const isCreateDisabled = !orgName.trim() || isOrgCreationInProgress

  return (
    <div className="bg-brand-neutral-50 shadow rounded-lg mt-6 p-6">
      {/* Create Organization Header */}
      <h2 className="text-xl font-bold">Create an organization</h2>
      <p className="mt-2 text-sm text-brand-neutral-600">
        Unlock collaboration features{!isSubscriptionExempt && ', centralize billing for your team,'} and manage organizational settings.
      </p>

      {/* Organization Form */}
      <OrganizationSetup
        orgName={orgName}
        setOrgName={setOrgName}
        emails={emails}
        setEmails={setEmails}
        emailValue={emailValue}
        setEmailValue={setEmailValue}
      />

      {/* Create Organization Button */}
      <div className="mt-6 flex items-center justify-end space-x-4">
        <button
          type="button"
          className={`flex items-center gap-2 rounded-md bg-brand-500 px-3.5 py-2.5 text-sm font-semibold text-white enabled:hocus:bg-brand-800 focus:outline-none disabled:cursor-not-allowed disabled:bg-true-gray-300`}
          onClick={handleCreateOrganization}
          disabled={isCreateDisabled}
        >
          Create my organization
          {isOrgCreationInProgress && <CircularProgressContinuousSized hexColor={'#eee'} size={20} thickness={4} />}
        </button>
      </div>
    </div>
  )
}

export default CreateOrganizationCard
